import React, { FC, useMemo } from "react";
import { ConfirmationRaw } from "./raw";
import { ConfirmationProps } from "./types";

import { StepProps } from "../ReviewSummary/components/InstructionsList/types";
import useCurrencyConfig from "../ReviewSummary/hooks/useCurrencyConfig";
import {
  useLanguage,
  useReturnsConfig,
} from "@narvar/nth-kit-returns-headless";
import { generateTokensFromObject } from "@narvar/nth-kit-returns-headless/dist/src/hooks/language/useLanguage";

export const Confirmation: FC<ConfirmationProps> = (props) => {
  const { isHipaaCompliant } = useReturnsConfig();
  const { locale, includeCurrency } = useCurrencyConfig();
  const langKeySuffix = isHipaaCompliant ? "Hipaa" : "";

  const {
    providerMoniker = "",
    refundMethod = "",
    location,
    type = "",
  } = props;

  const { value: provider } = useLanguage({
    langKey: `returnsMoniker${providerMoniker}DisplayName`,
    asString: true,
  });

  const replacementTokens = generateTokensFromObject({
    provider,
    location: location?.displayName,
  });
  const { value: Step1Title } = useLanguage({
    langKey: `returnsConfirmation${type}Step1Title`,
    asString: true,
    replacementTokens,
  });
  const { value: Step1Details } = useLanguage({
    langKey: `returnsConfirmation${type}Step1Details`,
    asString: true,
    replacementTokens,
  });
  const { value: Step2Title } = useLanguage({
    langKey: `returnsConfirmation${type}Step2Title${langKeySuffix}`,
    asString: true,
    replacementTokens,
  });
  const { value: Step2Details } = useLanguage({
    langKey: `returnsConfirmation${type}Step2Details`,
    asString: true,
    replacementTokens,
  });
  const { value: Step3Title } = useLanguage({
    langKey: `returnsConfirmation${type}Step3Title`,
    asString: true,
    replacementTokens,
  });
  const { value: Step3Details } = useLanguage({
    langKey: `returnsConfirmation${type}Step3Details`,
    asString: true,
    replacementTokens,
  });
  const { rawValue: providerLinkUrl } = useLanguage({
    langKey: `returnsMoniker${providerMoniker}LinkUrl`,
    asString: true,
  });
  const { value: providerLinkText } = useLanguage({
    langKey: `returnsMoniker${providerMoniker}LinkText`,
    asString: true,
    replacementTokens,
  });
  const refundStepTitle = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepTitle`,
  }).value;
  const refundStepDetails = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepDetails`,
  }).value;
  const refundStepAppStoreLink = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepAppStoreLink`,
  }).value;
  const refundStepAppStoreLinkText = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepAppStoreLinkText`,
  }).value;
  const refundStepGooglePlayLink = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepGooglePlayLink`,
  }).value;
  const refundStepGooglePlayLinkText = useLanguage({
    langKey: `returnsConfirmation${refundMethod}StepGooglePlayLinkText`,
  }).value;

  const steps: StepProps[] = useMemo(() => {
    const link =
      providerLinkText && providerLinkUrl
        ? { text: providerLinkText, url: providerLinkUrl }
        : undefined;
    const step2Link = type === "printerless_mail" && link ? [link] : [];
    const step3Link =
      (type === "mail" || type === "concierge") && link ? [link] : [];

    let ret: StepProps[] = [];
    if (refundMethod === "reshop") {
      ret = [
        ...ret,
        {
          title: refundStepTitle,
          details: refundStepDetails,
          links: [
            {
              text: refundStepAppStoreLinkText,
              url: refundStepAppStoreLink ?? "",
              image: "app_store",
            },
            {
              text: refundStepGooglePlayLinkText,
              url: refundStepGooglePlayLink ?? "",
              image: "google_play",
            },
          ],
        },
      ];
    }
    ret = [
      ...ret,
      { title: Step1Title, details: Step1Details },
      { title: Step2Title, details: Step2Details, links: step2Link },
    ];
    if (Step3Title) {
      ret = [
        ...ret,
        { title: Step3Title, details: Step3Details, links: step3Link },
      ];
    }
    return ret;
  }, [
    type,
    refundMethod,
    refundStepTitle,
    refundStepDetails,
    refundStepAppStoreLinkText,
    refundStepAppStoreLink,
    refundStepGooglePlayLinkText,
    refundStepGooglePlayLink,
    providerLinkText,
    providerLinkUrl,
    Step1Title,
    Step1Details,
    Step2Title,
    Step2Details,
    Step3Title,
    Step3Details,
  ]);

  return (
    <ConfirmationRaw
      isHipaaCompliant={isHipaaCompliant}
      locale={locale}
      includeCurrency={includeCurrency}
      {...props}
      returnSteps={steps}
    />
  );
};

export { ConfirmationRaw };
